// Searching Typesense from the browser, like the maps app does (`ts.js` in
// map-utils), with the search-only key and the public host the server puts in
// `window.NaturkartanConfig.typesense` (`NaturkartanWeb.Helper.App`).

const PER_PAGE_LIMIT = 250
// how often a failed request is tried again, and the ms between the tries
// (the defaults of Typesense's own client)
const RETRIES = 3
const RETRY_INTERVAL = 100

const config = () => window.NaturkartanConfig?.typesense || {}

// a failure worth trying again: the request didn't get through, or the
// server couldn't answer it (`response` is missing for a network error)
const isRetryable = (response) =>
  !response || response.status >= 500 || [408, 429].includes(response.status)

// resolves after `ms`, or rejects at once with the abort of `signal`
const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason)

    const abort = () => {
      window.clearTimeout(timeout)
      reject(signal.reason)
    }
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener(`abort`, abort)
      resolve()
    }, ms)

    signal?.addEventListener(`abort`, abort, { once: true })
  })

// `fetch`, tried again on network errors and server errors (`isRetryable`),
// never once the request is aborted
async function fetchWithRetries(url, options) {
  for (let attempt = 0; ; attempt++) {
    let response = null
    let error = null

    try {
      response = await window.fetch(url, options)
      if (response.ok || !isRetryable(response)) return response
    } catch (fetchError) {
      if (options.signal?.aborted) throw fetchError
      error = fetchError
    }

    if (attempt >= RETRIES) {
      if (error) throw error
      return response
    }

    await wait(RETRY_INTERVAL, options.signal)
  }
}

// one page of a search in `collection` (`site`, `guide`, …, without the
// environment suffix)
export async function searchTs({ collection, params, signal }) {
  const { host, apiKey, env } = config()
  if (!host || !apiKey) throw new Error(`Typesense is not configured`)

  const url = new URL(
    `${host}/collections/${collection}:${env}/documents/search`
  )
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== ``) url.searchParams.set(key, value)
  })

  const response = await fetchWithRetries(url, {
    headers: { "x-typesense-api-key": apiKey },
    signal,
  })

  if (!response.ok)
    throw new Error(`Could not search ${collection} (${response.status})`)

  return response.json()
}

// every hit of a search, up to `limit` (0: all of them), page by page
export async function searchTsAll({ collection, params, limit = 0, signal }) {
  const hits = []

  for (let page = 1; ; page++) {
    const data = await searchTs({
      collection,
      params: { ...params, page, per_page: PER_PAGE_LIMIT },
      signal,
    })

    hits.push(...data.hits)

    const wanted = limit ? Math.min(limit, data.found) : data.found
    if (hits.length >= wanted || !data.hits.length) return hits.slice(0, wanted)
  }
}

// The sprite icons of a site's pin (in the sprite the maps app deployed, see
// `featurizePoint` in map-utils' `ts.js`): its main category's, by its type,
// with a variant for some categories; and the one of its active pin.
export function sitePinIcons({ type, main_category_icon, main_icon_id }) {
  const handle = main_category_icon
  const categoryId = main_icon_id ? parseInt(main_icon_id) : null
  let icon = handle ? `${handle}-${type.toLowerCase()}` : `none`

  if (type.toLowerCase() == `place`) {
    if (categoryId == 177) icon += `-favorites`
    else if ([174, 130].includes(categoryId)) icon += `-nature-reserve`
    else if (categoryId == 129) icon += `-warning`
    else if (categoryId == 128) icon += `-attention`
  }

  return { icon, iconActive: `${handle || `none`}-active` }
}

// km between two consecutive points of a trail's shape taken for a gap
// between its parts (see `siteShapeLines`)
const TRAIL_PART_GAP_KM = 2

const distanceKm = ([lat1, lng1], [lat2, lng2]) => {
  const rad = Math.PI / 180
  const x = (lng2 - lng1) * rad * Math.cos(((lat1 + lat2) / 2) * rad)
  const y = (lat2 - lat1) * rad
  return Math.hypot(x, y) * 6371
}

// The lines of a site's `shape`, which the index flattens into one list of
// [lat, lng] points (`Naturkartan.Content.Site`): an area's rings, each one
// ending where it starts, or a trail's parts, which are told apart by the gap
// between them (a part may then be cut where its points are as far apart).
// As [lng, lat] lines for a `MultiLineString`.
export function siteShapeLines({ type, shape }) {
  if (!Array.isArray(shape) || shape.length < 2) return []

  const isArea = type.toLowerCase() == `area`
  const lines = [[]]

  shape.forEach((point, i) => {
    let line = lines.at(-1)
    const previous = shape[i - 1]

    if (!isArea && previous && distanceKm(previous, point) > TRAIL_PART_GAP_KM)
      lines.push((line = []))

    line.push([point[1], point[0]])

    const [start] = line
    const isRingClosed =
      isArea && line.length > 2 && start[0] == point[1] && start[1] == point[0]

    if (isRingClosed && i < shape.length - 1) lines.push([])
  })

  return lines.filter((line) => line.length > 1)
}

// The published sites of an organization within `radiusKm` of a position,
// nearest first, as GeoJSON features (`id` being the site's id): `points`,
// their pins (`title`, `icon` and `iconActive` in the properties), and
// `shapes`, the lines of the trails' and areas' shapes (`kind`: `trail` or
// `area`)
export async function fetchTsSitesNearby({
  lat,
  lng,
  radiusKm,
  organizationId,
  locale = `sv`,
  limit = 0,
  signal,
}) {
  const hits = await searchTsAll({
    collection: `site`,
    limit,
    signal,
    params: {
      q: `*`,
      filter_by: [
        `organization_id:=${organizationId}`,
        `published:=true`,
        `marker_point:(${lat},${lng},${radiusKm} km)`,
      ].join(` && `),
      sort_by: `marker_point(${lat},${lng}):asc`,
      include_fields: [
        `id`,
        `type`,
        `name_sv`,
        `name_${locale}`,
        `marker_point`,
        `shape`,
        `main_category_icon`,
        `main_icon_id`,
      ].join(`,`),
    },
  })

  const sites = hits.map(({ document: site }) => site)

  const points = sites
    .filter((site) => site.marker_point)
    .map((site) => ({
      type: `Feature`,
      id: parseInt(site.id),
      geometry: {
        type: `Point`,
        coordinates: [site.marker_point[1], site.marker_point[0]],
      },
      properties: {
        id: parseInt(site.id),
        title: site[`name_${locale}`] || site.name_sv,
        ...sitePinIcons(site),
      },
    }))

  const shapes = sites
    .filter(({ type }) => [`trail`, `area`].includes(type.toLowerCase()))
    .map((site) => ({ site, lines: siteShapeLines(site) }))
    .filter(({ lines }) => lines.length)
    .map(({ site, lines }) => ({
      type: `Feature`,
      id: parseInt(site.id),
      geometry: { type: `MultiLineString`, coordinates: lines },
      properties: { id: parseInt(site.id), kind: site.type.toLowerCase() },
    }))

  return { points, shapes }
}
